import type { TestInfoRow } from '@/pages/home/aboutLc.content'

interface LcTestInfoBlockProps {
  badge: string
  title: string
  description: string
  panelTitle: string
  rows: TestInfoRow[]
  imageUrl: string
  imageUrlMobile: string
  imageAlt: string
  /** Section background color hex (e.g. '#1c52a8') */
  bgColor: string
  /** Bookshape notch color — matches the previous section's bg so it looks like it spills down */
  topColor: string
  /** Optional id for hash-scroll targeting (e.g. "chapter-test", "cumulative-test") */
  sectionId?: string
}

export function LcTestInfoBlock({
  badge,
  title,
  description,
  panelTitle,
  rows,
  imageUrl,
  imageUrlMobile,
  imageAlt,
  bgColor,
  topColor,
  sectionId,
}: LcTestInfoBlockProps) {
  return (
    <section
      id={sectionId}
      data-marketing-section=""
      className="relative w-full py-14 lg:py-20 tracking-[-0.6px]"
      style={{ backgroundColor: bgColor }}
    >
      {/* Bookshape — notch pointing down, colored to match the previous section so it looks like the band above spills into this one */}
      <svg
        aria-hidden
        viewBox="0 0 146 36"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute left-1/2 -translate-x-1/2 top-0 w-[110px] lg:w-[146px] h-auto"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M146 0C87.044 0 72.0224 35.9464 72 36C72 36 52 0 0 0H146Z"
          fill={topColor}
        />
      </svg>

      <div className="mx-auto max-w-[1136px] px-6 lg:px-0 flex flex-col items-center gap-14 lg:gap-20">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 lg:gap-6 text-center w-full">
          <div
            className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-white flex items-center justify-center font-bold text-[20px] lg:text-[24px] leading-[1.4]"
            style={{ color: bgColor }}
          >
            {badge}
          </div>
          <div className="flex flex-col gap-3 lg:gap-4 items-center">
            <h2 className="font-bold text-white text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
              {title}
            </h2>
            <p className="font-medium lg:font-normal text-white text-[20px] lg:text-[24px] leading-[1.5] lg:leading-[1.4]">
              {description}
            </p>
          </div>
        </div>

        {/* Card panel */}
        <div data-testid="text-image-split" className="w-full max-w-[360px] lg:max-w-none lg:w-[944px] bg-white rounded-[24px] shadow-[0px_4px_8px_rgba(0,0,0,0.15)] p-4 pb-6 lg:p-10 flex flex-col lg:flex-row gap-6 lg:gap-10 items-center lg:justify-center">
          {/* Image — mobile first, desktop right */}
          <picture className="order-1 lg:order-2 w-full lg:w-[360px] flex justify-center lg:block">
            <source media="(min-width: 1024px)" srcSet={imageUrl} />
            <img
              src={imageUrlMobile}
              alt={imageAlt}
              className="w-full h-[184px] lg:h-[240px] object-cover rounded-[16px] lg:rounded-[24px] bg-qb-bg-secondary"
            />
          </picture>

          {/* Text panel */}
          <div className="order-2 lg:order-1 flex-1 flex flex-col gap-4 lg:gap-6 items-start w-full">
            {/* Panel title — desktop only per Figma */}
            <h3 className="hidden lg:block font-bold text-qb-text-high text-[28px] leading-[1.4]">
              {panelTitle}
            </h3>

            <dl className="flex flex-col gap-2.5 lg:gap-2.5 w-full">
              {rows.map((row) => (
                <div key={row.label} className="flex gap-4 items-start w-full">
                  <dt className="shrink-0 w-[65px] lg:w-[72px] font-medium text-qb-primary text-[18px] lg:text-[20px] leading-[1.5]">
                    {row.label}
                  </dt>
                  <dd className="flex-1 font-normal text-qb-text-mid text-[18px] lg:text-[20px] leading-[1.5]">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  )
}
